import {
  ArrowRight,
  CalendarCheck,
  CheckCircle2,
  PackageCheck,
  Pill,
  ShieldCheck,
  Zap,
} from 'lucide-react';
import Button from '../../common/Button';
import Container from '../../common/Container';
import ScrollReveal from '../common/ScrollReveal';

export const PharmacyHero = () => {
  const scrollTo = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -80;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <section
      id="about"
      className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden bg-gradient-to-b from-[#0D7893]/5 via-white to-white"
    >
      {/* Background decorative glows */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-gradient-to-tr from-[#0D7893]/15 to-emerald-200/20 blur-3xl rounded-full pointer-events-none -z-10" />
      <div className="absolute top-1/3 -right-20 w-96 h-96 bg-[#0D7893]/10 blur-3xl rounded-full pointer-events-none -z-10" />
      <div className="absolute bottom-10 -left-20 w-96 h-96 bg-teal-400/10 blur-3xl rounded-full pointer-events-none -z-10" />

      <Container>
        <div className="text-center max-w-4xl mx-auto space-y-6">
          {/* Eyebrow Badge */}
          <ScrollReveal direction="down" delay={100}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 backdrop-blur-md border border-[#0D7893]/30 shadow-sm text-xs md:text-sm font-semibold text-[#0D7893]">
              <span className="flex h-2 w-2 rounded-full bg-[#0D7893] animate-ping" />
              <Pill className="w-4 h-4 text-[#0D7893]" />
              <span>Divine Pharmacy ERP</span>
              <span className="text-slate-300">|</span>
              <span className="text-slate-600 font-normal">Medical & Chemist Software</span>
            </div>
          </ScrollReveal>

          {/* Main Title */}
          <ScrollReveal direction="up" delay={200}>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-6xl font-extrabold text-slate-900 tracking-tight leading-[1.15]">
              Pharmacy ERP &{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0D7893] via-[#1aa3c4] to-[#085063]">
                Precision Stock Control
              </span>
            </h1>
          </ScrollReveal>

          {/* Subtitle */}
          <ScrollReveal direction="up" delay={300}>
            <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed font-normal">
              A complete, pharmacy solution built for retail medical shops, chemist outlets, and
              pharmacies. Operate independently with lightning-fast POS counter billing, multi-batch
              expiry radar, automated purchase orders, and statutory GST accounting without
              requiring any external hospital software.
            </p>
          </ScrollReveal>

          {/* Call to Actions */}
          <ScrollReveal direction="up" delay={400}>
            <div className="flex flex-wrap items-center justify-center gap-4 pt-2">
              <Button
                size="lg"
                onClick={() => scrollTo('modules')}
                className="gap-2 group shadow-xl shadow-[#0D7893]/25"
              >
                <span>Explore Pharmacy Modules</span>
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </Button>
              <Button
                size="lg"
                variant="white"
                onClick={() => scrollTo('contact')}
                className="gap-2"
              >
                <CalendarCheck className="w-5 h-5 text-[#0D7893]" />
                <span>Schedule a Live Demo</span>
              </Button>
            </div>
          </ScrollReveal>

          {/* Trust Highlights */}
          <ScrollReveal direction="up" delay={500}>
            <div className="pt-6 flex flex-wrap items-center justify-center gap-6 sm:gap-8 text-xs sm:text-sm font-medium text-slate-600">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#0D7893]" />
                <span>Barcode POS Dispensing</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#0D7893]" />
                <span>Batch & Expiry Precision</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#0D7893]" />
                <span>Reports & GST Invoicing</span>
              </div>
            </div>
          </ScrollReveal>
        </div>

        {/* Hero Showcase Frame with Floating Badges */}
        <div className="mt-12 md:mt-16 relative max-w-5xl mx-auto">
          <ScrollReveal direction="zoom" delay={300} duration={900}>
            <div className="relative rounded-2xl md:rounded-3xl p-2 sm:p-3 bg-gradient-to-b from-[#0D7893]/25 via-slate-200/50 to-white shadow-2xl shadow-[#0D7893]/20 border border-[#0D7893]/20">
              {/* Window Header */}
              <div className="bg-slate-900 rounded-t-xl md:rounded-t-2xl px-4 py-3 flex items-center justify-between border-b border-slate-800">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-rose-500/90" />
                  <div className="w-3 h-3 rounded-full bg-amber-500/90" />
                  <div className="w-3 h-3 rounded-full bg-emerald-500/90" />
                  <span className="ml-2 text-xs font-mono text-slate-400 hidden sm:inline-block">
                    Divine Pharmacy ERP · Chemist & Retail System
                  </span>
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-400">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 font-medium">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> POS
                    Counter Online
                  </span>
                </div>
              </div>

              {/* Cover Screenshot Container */}
              <div className="relative overflow-hidden rounded-b-xl md:rounded-b-2xl bg-white border border-slate-100 group">
                <img
                  src="/img/svg/pharmacy_dashboard.png"
                  alt="Divine Pharmacy ERP Dashboard"
                  className="w-full h-auto object-cover transform transition-transform duration-700 group-hover:scale-[1.01]"
                />

                {/* Subtle gradient overlay */}
                <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-slate-900/10 to-transparent pointer-events-none" />
              </div>
            </div>
          </ScrollReveal>

          {/* Floating Card 1: Fast Dispensing */}
          <div className="hidden md:flex absolute -left-8 -bottom-6 z-20 items-center gap-3.5 bg-white/95 backdrop-blur-md p-4 rounded-2xl shadow-xl shadow-slate-900/10 border border-[#0D7893]/20 animate-float">
            <div className="p-2.5 rounded-xl bg-[#0D7893]/10 text-[#0D7893]">
              <Zap className="w-6 h-6" />
            </div>
            <div>
              <div className="text-xs text-slate-500 font-medium">Rapid POS Checkout</div>
              <div className="text-sm font-bold text-slate-900">Sub-30s Counter Invoicing</div>
              <div className="text-[11px] text-emerald-600 font-semibold flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Barcode & Generic Fast
                Match
              </div>
            </div>
          </div>

          {/* Floating Card 2: Expiry & Stock Safety */}
          <div className="hidden md:flex absolute -right-8 -top-6 z-20 items-center gap-3.5 bg-white/95 backdrop-blur-md p-4 rounded-2xl shadow-xl shadow-slate-900/10 border border-[#0D7893]/20 animate-float [animation-delay:2s]">
            <div className="p-2.5 rounded-xl bg-[#0D7893]/10 text-[#0D7893]">
              <PackageCheck className="w-6 h-6" />
            </div>
            <div>
              <div className="text-xs text-slate-500 font-medium">Expiry Risk Shield</div>
              <div className="text-sm font-bold text-slate-900">Zero Stockout Discrepancy</div>
              <div className="text-[11px] text-[#0D7893] font-semibold flex items-center gap-1">
                <ShieldCheck className="w-3.5 h-3.5" /> 30/60/90 Day Expiry Radar
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
};

export default PharmacyHero;
